/**
 * bundle-builder-direct.js
 * Section-only: Subscribe ATC with 30-day selling plan + cart-tier card pricing.
 * Loaded only from sections/bundle-builder-direct.liquid.
 * Boots a BundleBuilderDirect instance per [data-hook="bundle-builder-direct"] root.
 */
;(function () {
  'use strict'

  function ready(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn)
    } else {
      fn()
    }
  }

  function formatMoney(cents) {
    const value = Math.max(0, Number(cents) || 0) / 100
    const format = (window.theme && window.theme.moneyFormat) || '${{amount}}'
    if (typeof window.Shopify !== 'undefined' && typeof window.Shopify.formatMoney === 'function') {
      try {
        return window.Shopify.formatMoney(cents, format)
      } catch (e) {
        /* fall through */
      }
    }
    if (value % 1 === 0) return `$${value.toFixed(0)}`
    return `$${value.toFixed(2)}`
  }

  function formatRewardMoney(cents) {
    const value = Math.max(0, cents || 0) / 100
    return value % 1 === 0 ? `$${value.toFixed(0)}` : `$${value.toFixed(2)}`
  }

  function applyPercentOffCents(priceCents, percent) {
    const price = Math.max(0, Math.round(Number(priceCents) || 0))
    const discountPercent = Math.max(0, parseFloat(percent) || 0)
    if (!discountPercent) return price
    return price - Math.floor((price * discountPercent) / 100)
  }

  function getRewardConfig(root) {
    const configElement =
      (root && root.querySelector('[data-subscription-reward-config]')) ||
      document.querySelector('[data-subscription-reward-config]')
    if (!configElement) return null
    try {
      const parsed = JSON.parse(configElement.textContent || '{}')
      const defaultAwayMessages = [
        "You're {price} away from 10% off",
        "You're {price} away from free U.S. shipping",
        '',
        '',
      ]
      return {
        thresholds: (parsed.thresholds || []).map((t) => parseInt(t, 10) || 0).slice(0, 4),
        shippingThreshold: parseInt(parsed.shippingThreshold, 10) || 0,
        awayMessages: defaultAwayMessages.map((fallback, index) => {
          const message = parsed.awayMessages?.[index]
          return typeof message === 'string' && message.trim() ? message : fallback
        }),
        unlockMessages: parsed.unlockMessages || [],
        tier3DiscountPercentage: parseFloat(parsed.tier3DiscountPercentage) || 11,
        tier4DiscountPercentage: parseFloat(parsed.tier4DiscountPercentage) || 0,
        toastMessages: (parsed.toastMessages || [])
          .slice(0, 4)
          .map((message) => (typeof message === 'string' ? message.trim() : '')),
        nextToastMessages: (parsed.nextToastMessages || [])
          .slice(0, 4)
          .map((message) => (typeof message === 'string' ? message.trim() : '')),
      }
    } catch (error) {
      console.error('[bundle-builder-direct] Invalid reward config', error)
      return null
    }
  }

  function isFreeSubscriptionGift(item) {
    const props = item?.properties || {}
    return Boolean(
      props._subscription_gwp_free_gift || props._subscription_free_gift || props._single_subscription_free_gift
    )
  }

  const subscriptionPlanIdsByHandle = new Map()

  function getCartItemHandle(item) {
    if (item?.handle) return item.handle
    const productPath = String(item?.url || '').split('/products/')[1]
    return productPath ? productPath.split(/[?#]/)[0] : ''
  }

  function getSubscribeAndSavePlanIds(handle) {
    if (!handle) return Promise.resolve(new Set())
    if (subscriptionPlanIdsByHandle.has(handle)) return subscriptionPlanIdsByHandle.get(handle)

    const rootPath = window.Shopify?.routes?.root || '/'
    const request = fetch(`${rootPath}products/${encodeURIComponent(handle)}.js`)
      .then((response) => {
        if (!response.ok) throw new Error(`Unable to load selling plans for ${handle}`)
        return response.json()
      })
      .then((product) => {
        const planIds = new Set()
        ;(product.selling_plan_groups || []).forEach((group) => {
          const targetGroupName = String(window.themeSettings?.subscriptionSellingPlanGroupName || 'subscribe and save')
            .trim()
            .toLowerCase()
          if (
            String(group.name || '')
              .trim()
              .toLowerCase() !== targetGroupName
          )
            return
          ;(group.selling_plans || []).forEach((plan) => {
            const id = parseInt(plan.id, 10)
            if (id) planIds.add(id)
          })
        })
        return planIds
      })
      .catch((error) => {
        console.error('[bundle-builder-direct] Unable to resolve Subscribe and Save plans', error)
        return new Set()
      })
    subscriptionPlanIdsByHandle.set(handle, request)
    return request
  }

  async function isSubscribeAndSaveLine(item) {
    const sellingPlanId =
      parseInt(item?.selling_plan_allocation?.selling_plan?.id, 10) ||
      parseInt(item?.selling_plan_allocation?.selling_plan_id, 10) ||
      parseInt(item?.selling_plan, 10)
    if (!sellingPlanId) return false
    const eligibleIds = await getSubscribeAndSavePlanIds(getCartItemHandle(item))
    return eligibleIds.has(sellingPlanId)
  }

  function getLineDisplayPrice(item) {
    if (item.final_line_price != null) return Number(item.final_line_price) || 0
    if (item.line_price != null) return Number(item.line_price) || 0
    return (Number(item.final_price || item.price) || 0) * (Number(item.quantity) || 1)
  }

  function getVariantSaleDiscount(root, item) {
    const variantId = String(item?.variant_id || '')
    if (!root || !variantId) return 0
    const cards = root.querySelectorAll('[data-hook="subscribe-card"]')
    for (const card of cards) {
      if (String(card.getAttribute('data-product-id') || '') !== String(item.product_id || '')) continue
      const variant = parseVariantPricing(card).find((entry) => String(entry.id) === variantId)
      if (variant) return Number(variant.saleDiscount) || 0
    }
    return 0
  }

  function getLineThresholdPrice(root, item) {
    let line = item.original_line_price
    if (line == null) line = (Number(item.original_price) || 0) * (Number(item.quantity) || 1)
    if (line == null) line = getLineDisplayPrice(item)
    line = Number(line) || 0

    const saleDiscount = parseInt(item.properties?._ssd, 10) || getVariantSaleDiscount(root, item)
    if (saleDiscount > 0) line -= Math.floor((line * saleDiscount) / 100)
    return Math.max(0, line)
  }

  // Match cart-tier-progress: a subscribed deluxe kit is one reward entry,
  // priced from its grouped child lines using the primary kit discount only.
  function getDeluxeKitThresholdPrice(item, primaryPercent) {
    let line = item.original_line_price
    if (line == null) line = (Number(item.original_price) || 0) * (Number(item.quantity) || 1)
    if (line == null) line = getLineDisplayPrice(item)
    line = Number(line) || 0

    let subtractedPrimary = 0
    ;(item.discounts || []).forEach((discount) => {
      const title = String(discount.title || '').toLowerCase()
      const isAdditional = title.includes('additional') || title.includes('extra')
      const isPrimaryKitDiscount = title.includes('deluxe kit subscription') || title.includes('deluxe starter kit')
      if (!isAdditional && isPrimaryKitDiscount) {
        const amount = Number(discount.amount) || 0
        line -= amount
        subtractedPrimary += amount
      }
    })
    const displayLine = getLineDisplayPrice(item)
    if (subtractedPrimary === 0 && primaryPercent > 0 && line > displayLine && displayLine > 0) {
      const percentHundredths = Math.round(primaryPercent * 100)
      return Math.max(0, line - Math.floor((line * percentHundredths) / 10000))
    }
    return Math.max(0, line)
  }

  async function getSubscriptionTotals(root, cart) {
    if (!cart || !Array.isArray(cart.items)) {
      return { displayTotal: 0, thresholdTotal: 0 }
    }
    let displayTotal = 0
    let thresholdTotal = 0
    const items = cart.items
    const primaryPercent =
      parseFloat(document.querySelector('[data-dsk-widget]')?.getAttribute('data-dsk-subscription-discount')) || 0
    const subscribedDeluxeKitIds = new Set()
    const eligibleLines = await Promise.all(
      items.map(async (item) => {
        const deluxeId =
          item.properties?._deluxeId || (String(item.properties?._id || '').includes('kit') ? item.properties._id : '')
        return {
          item,
          deluxeId: deluxeId ? String(deluxeId) : '',
          eligible: await isSubscribeAndSaveLine(item),
        }
      })
    )
    eligibleLines.forEach(({ deluxeId, eligible }) => {
      if (deluxeId && eligible) subscribedDeluxeKitIds.add(deluxeId)
    })

    const processedDeluxeKitIds = new Set()
    for (const { item, deluxeId: eligibleDeluxeId, eligible } of eligibleLines) {
      const props = item.properties || {}
      const deluxeId = String(props._deluxeId || (String(props._id || '').includes('kit') ? props._id : '') || '')
      const itemIsEligible = subscribedDeluxeKitIds.has(deluxeId) || eligible

      if (!itemIsEligible || isFreeSubscriptionGift(item)) continue

      const isDeluxeKitLine = Boolean(
        (eligibleDeluxeId && subscribedDeluxeKitIds.has(eligibleDeluxeId)) ||
          props._deluxekit ||
          props._deluxekit_master ||
          String(props._id || '').includes('kit')
      )
      if (isDeluxeKitLine && deluxeId) {
        if (processedDeluxeKitIds.has(deluxeId)) continue
        processedDeluxeKitIds.add(deluxeId)
        const groupedTotal = items.reduce((total, groupedItem) => {
          if (String(groupedItem.properties?._deluxeId || '') !== deluxeId) return total
          if (isFreeSubscriptionGift(groupedItem)) return total
          return total + getDeluxeKitThresholdPrice(groupedItem, primaryPercent)
        }, 0)
        displayTotal += groupedTotal
        thresholdTotal += groupedTotal
        continue
      }

      displayTotal += getLineDisplayPrice(item)
      const line = getLineThresholdPrice(root, item)
      thresholdTotal += line
    }
    return { displayTotal, thresholdTotal }
  }

  function isShippingUnlockedByMixedCart(displayTotal, cartSubtotal, shippingThreshold) {
    return displayTotal > 0 && shippingThreshold > 0 && cartSubtotal >= shippingThreshold
  }

  function getActiveTiers(thresholds, displayTotal, thresholdTotal, cartSubtotal, shippingThreshold) {
    const [t1 = 0, t2 = 0, t3 = 0, t4 = 0] = thresholds || []
    const tier2Complete =
      isShippingUnlockedByMixedCart(displayTotal, cartSubtotal, shippingThreshold) || (t2 > 0 && displayTotal >= t2)
    let active = 0
    if (t1 > 0 && displayTotal >= t1) active = 1
    if (tier2Complete) active = 2
    if (t3 > 0 && thresholdTotal >= t3) active = 3
    if (t4 > 0 && thresholdTotal >= t4) active = 4
    return active
  }

  function getProgressPercent(thresholds, displayTotal, thresholdTotal, cartSubtotal, shippingThreshold) {
    if (!thresholds || thresholds.length < 4 || thresholds.some((t) => t <= 0)) return 0
    const [t1, t2, t3, t4] = thresholds
    const shippingUnlocked = isShippingUnlockedByMixedCart(displayTotal, cartSubtotal, shippingThreshold)
    const tier2UnlockedByCartTotalOnly = shippingUnlocked && displayTotal < t2
    const active = getActiveTiers(thresholds, displayTotal, thresholdTotal, cartSubtotal, shippingThreshold)
    const tier2Complete = shippingUnlocked || (t2 > 0 && displayTotal >= t2)

    let progress = 0
    if (active === 4) progress = 100
    else if (tier2UnlockedByCartTotalOnly) progress = 33.3333
    else if (thresholdTotal >= t3 && t4 > t3) {
      progress = 66.6667 + ((thresholdTotal - t3) / (t4 - t3)) * 33.3333
    } else if (thresholdTotal >= t2 && t3 > t2) {
      progress = 33.3333 + ((thresholdTotal - t2) / (t3 - t2)) * 33.3333
    } else if (displayTotal >= t1 && t2 > t1) {
      progress = ((displayTotal - t1) / (t2 - t1)) * 33.3333
    } else if (displayTotal > 0 && t1 > 0) progress = (displayTotal / t1) * 33.3333
    else if (tier2Complete) progress = 33.3333

    if (progress > 100) return 100
    if (progress < 0) return 0
    return progress
  }

  function getExtraDiscountPercent(activeTiers, config) {
    if (activeTiers >= 4 && (config?.tier4DiscountPercentage || 0) > 0) {
      return config.tier4DiscountPercentage
    }
    if (activeTiers >= 3 && (config?.tier3DiscountPercentage || 0) > 0) {
      return config.tier3DiscountPercentage
    }
    return 0
  }

  function logicalRewardProgress(layout, bar) {
    if (layout?.dataset.rewardProgress != null && layout.dataset.rewardProgress !== '') {
      return parseFloat(layout.dataset.rewardProgress) || 0
    }
    return parseFloat(bar?.style.height) || 0
  }

  function placeMobileRewardRails(root) {
    if (!root) return
    root.querySelectorAll('[data-subscription-reward-layout="mobile"]').forEach((layout) => {
      const rail = layout.querySelector('[data-reward-rail]')
      const bar = layout.querySelector('[data-subscription-reward-progress]')
      const dots = [...layout.querySelectorAll('[data-subscription-reward-dot]')]
      if (!rail || !bar || dots.length < 2) return
      const parent = rail.offsetParent
      if (!parent) return
      const parentBox = parent.getBoundingClientRect()
      if (parentBox.height < 1) return

      const boxes = dots.map((dot) => dot.getBoundingClientRect())
      const centers = boxes.map((box) => box.top + box.height / 2 - parentBox.top)
      const first = centers[0]
      const last = centers[centers.length - 1]
      const centerX = boxes[0].left + boxes[0].width / 2 - parentBox.left
      rail.style.top = `${Math.max(0, first)}px`
      rail.style.bottom = `${Math.max(0, parentBox.height - last)}px`
      rail.style.left = `${centerX - 0.5}px`
      bar.style.width = '100%'

      const span = last - first
      const progress = Math.max(0, Math.min(100, logicalRewardProgress(layout, bar)))
      layout.dataset.rewardProgress = String(progress)
      if (span <= 0) {
        bar.style.height = '0%'
        return
      }

      const segmentShare = 100 / (centers.length - 1)
      let filled = 0
      let remaining = progress
      for (let index = 0; index < centers.length - 1; index += 1) {
        const segment = centers[index + 1] - centers[index]
        if (remaining + 0.001 >= segmentShare) {
          filled += segment
          remaining -= segmentShare
        } else {
          filled += segment * (remaining / segmentShare)
          break
        }
      }
      bar.style.height = `${(filled / span) * 100}%`
    })
  }

  function setRewardState(element, stateName) {
    element.setAttribute('data-state', stateName)
    element.querySelectorAll('[data-reward-state-view]').forEach((view) => {
      view.classList.toggle('hidden', view.getAttribute('data-reward-state-view') !== stateName)
    })
  }

  async function updateRewardWidgets(root, cart, config, totals) {
    if (!config) return
    const { displayTotal, thresholdTotal } = totals || (await getSubscriptionTotals(root, cart))
    const cartSubtotal = cart?.items_subtotal_price || 0
    const widgets = root.querySelectorAll('[data-subscription-reward-widget]')

    widgets.forEach((widget) => {
      const activeTiers = getActiveTiers(
        config.thresholds,
        displayTotal,
        thresholdTotal,
        cartSubtotal,
        config.shippingThreshold
      )
      const progress = getProgressPercent(
        config.thresholds,
        displayTotal,
        thresholdTotal,
        cartSubtotal,
        config.shippingThreshold
      )

      widget.querySelectorAll('[data-subscription-reward-value]').forEach((el) => {
        el.textContent = formatRewardMoney(displayTotal)
      })

      widget.querySelectorAll('[data-subscription-reward-layout="mobile"]').forEach((layout) => {
        layout.dataset.rewardProgress = String(progress)
      })
      widget.querySelectorAll('[data-subscription-reward-progress]').forEach((bar) => {
        const layout = bar.closest('[data-subscription-reward-layout]')
        if (layout?.getAttribute('data-subscription-reward-layout') === 'mobile') return
        bar.style.width = `${progress}%`
      })
      placeMobileRewardRails(widget)

      config.thresholds.forEach((threshold, index) => {
        const tier = index + 1
        const tierState = tier < activeTiers ? 'unlocked' : tier === activeTiers ? 'active' : 'locked'
        const progressTotal = tier >= 3 ? thresholdTotal : displayTotal
        const remaining = Math.max(0, threshold - progressTotal)
        const awayTemplate = config.awayMessages[index]
        const awayMessage =
          typeof awayTemplate === 'string' ? awayTemplate.split('{price}').join(formatRewardMoney(remaining)) : ''
        const unlockMessage = config.unlockMessages[index] || 'Unlocked'

        widget.querySelectorAll(`[data-subscription-reward-tier="${tier}"]`).forEach((tierEl) => {
          setRewardState(tierEl, tierState)
          if (awayMessage) {
            tierEl.querySelectorAll('[data-reward-away-message]').forEach((el) => {
              el.textContent = awayMessage
            })
          }
          tierEl.querySelectorAll('[data-reward-unlock-message]').forEach((el) => {
            el.textContent = unlockMessage
          })
        })

        widget.querySelectorAll(`[data-subscription-reward-dot="${tier}"]`).forEach((dot) => {
          dot.setAttribute('data-state', tierState)
          dot.querySelector('[data-reward-icon="complete"]')?.classList.toggle('hidden', tierState === 'locked')
          dot.querySelector('[data-reward-icon="locked"]')?.classList.toggle('hidden', tierState !== 'locked')
        })
      })
    })
  }

  function parseVariantPricing(card) {
    const el = card.querySelector('[data-subscribe-variant-pricing]')
    if (!el) return []
    try {
      return JSON.parse(el.textContent || '[]')
    } catch (e) {
      return []
    }
  }

  function getSelectedVariantId(card) {
    const select = card.querySelector('[data-hook="subscribe-variant"]')
    if (select) return parseInt(select.value, 10) || 0
    const hidden = card.querySelector('[data-hook="subscribe-variant-id"]')
    if (hidden) return parseInt(hidden.value, 10) || 0
    const selectedPill = card.querySelector(
      '[data-hook="subscribe-variant-pill"][aria-selected="true"]:not([disabled])'
    )
    if (selectedPill) return parseInt(selectedPill.getAttribute('data-variant-id'), 10) || 0
    const pricing = parseVariantPricing(card)
    return pricing[0]?.id || 0
  }

  function isThirtyDayPlanName(name) {
    const normalized = String(name || '')
      .toLowerCase()
      .replace(/deliver(?:y)? every\s+/g, '')
      .replace(/ships? every\s+/g, '')
      .trim()
    if (/60|90|2 month|3 month|two month|three month/.test(normalized)) return false
    return (
      normalized === '1 month' ||
      normalized === 'month' ||
      normalized.includes('30 day') ||
      normalized.includes('30-day')
    )
  }

  function thirtyDayPlanId(plans, fallbackPlan) {
    const named = (plans || []).find((plan) => isThirtyDayPlanName(plan?.name))
    if (named?.id) return parseInt(named.id, 10) || 0
    return parseInt(fallbackPlan, 10) || 0
  }

  function sellingPlanForVariant(root, variantId, fallbackPlan) {
    const wanted = String(variantId || '')
    const cards = root?.querySelectorAll?.('[data-hook="subscribe-card"]') || []
    for (const card of cards) {
      const pricing = parseVariantPricing(card)
      const variant = pricing.find((entry) => String(entry.id) === wanted)
      if (!variant) continue
      const planId = thirtyDayPlanId(variant.sellingPlans, fallbackPlan)
      if (planId) return planId
    }
    return parseInt(fallbackPlan, 10) || 0
  }

  function getVariantSellingPlan(card, variantId, fallbackPlan) {
    const pricing = parseVariantPricing(card)
    const variant = pricing.find((v) => String(v.id) === String(variantId))
    const fromPlans = thirtyDayPlanId(variant?.sellingPlans, 0)
    if (fromPlans) return fromPlans

    const fromVariant = parseInt(variant?.sellingPlan, 10)
    if (fromVariant) return fromVariant

    const select = card.querySelector('[data-hook="subscribe-variant"]')
    if (select) {
      const option = [...select.options].find((opt) => String(opt.value) === String(variantId))
      const fromOption = parseInt(option?.dataset?.sellingPlan, 10)
      if (fromOption) return fromOption
    }

    const pill = card.querySelector(`[data-hook="subscribe-variant-pill"][data-variant-id="${variantId}"]`)
    const fromPill = parseInt(pill?.getAttribute('data-selling-plan'), 10)
    if (fromPill) return fromPill

    const hidden = card.querySelector('[data-hook="subscribe-variant-id"]')
    const fromHidden = parseInt(hidden?.getAttribute('data-selling-plan'), 10)
    if (fromHidden) return fromHidden

    return parseInt(fallbackPlan, 10) || parseInt(card.getAttribute('data-selling-plan'), 10) || 0
  }

  let lastKnownCart = null
  let suppressCartNetworkSync = 0
  let cartRevision = 0

  function getLineSellingPlanId(item) {
    return (
      parseInt(item?.selling_plan_allocation?.selling_plan?.id, 10) ||
      parseInt(item?.selling_plan_allocation?.selling_plan_id, 10) ||
      parseInt(item?.selling_plan, 10) ||
      0
    )
  }

  function getCardDefaultSellingPlan(card, variantId, fallbackPlan) {
    const id = variantId || getSelectedVariantId(card)
    const fallback =
      fallbackPlan ||
      parseInt(card.getAttribute('data-preferred-selling-plan'), 10) ||
      parseInt(card.getAttribute('data-selling-plan'), 10) ||
      0
    return getVariantSellingPlan(card, id, fallback)
  }

  function isDefaultSubscriptionLine(card, item, fallbackPlan) {
    if (!item || isGiftCartLine(item)) return false
    const defaultPlan = getCardDefaultSellingPlan(card, item.variant_id, fallbackPlan)
    const linePlan = getLineSellingPlanId(item)
    return Boolean(defaultPlan && linePlan && String(linePlan) === String(defaultPlan))
  }

  function applyVariantSelection(card, pill, defaultSellingPlan) {
    if (!card || !pill || pill.disabled) return

    card.querySelectorAll('[data-hook="subscribe-variant-pill"]').forEach((btn) => {
      btn.setAttribute('aria-selected', btn === pill ? 'true' : 'false')
    })

    const variantId = pill.getAttribute('data-variant-id')
    const planId =
      parseInt(pill.getAttribute('data-selling-plan'), 10) || getVariantSellingPlan(card, variantId, defaultSellingPlan)
    const hidden = card.querySelector('[data-hook="subscribe-variant-id"]')
    if (hidden) {
      hidden.value = String(variantId || '')
      if (planId) hidden.setAttribute('data-selling-plan', String(planId))
    }

    if (planId) card.setAttribute('data-selling-plan', String(planId))
    if (variantId) card.dataset.activeVariantId = String(variantId)

    updateCardImage(card, pill.getAttribute('data-image'))

    const extra = parseFloat(card.dataset.extraDiscountPercent) || 0
    updateCardPrice(card, extra)
  }

  function variantImageSrcset(src) {
    const withWidth = (width) => {
      if (/[?&]width=\d+/.test(src)) return src.replace(/([?&])width=\d+/, `$1width=${width}`)
      return `${src}${src.includes('?') ? '&' : '?'}width=${width}`
    }
    return [200, 335, 400, 670, 800].map((width) => `${withWidth(width)} ${width}w`).join(', ')
  }

  function updateCardImage(card, src) {
    if (!card || !src) return
    const srcset = variantImageSrcset(src)
    // Variant changes update the main image only. The hover image stays the
    // product's second image, matching collection cards.
    card.querySelectorAll('.bundler-direct-card__img--default').forEach((img) => {
      img.src = src
      img.srcset = srcset
    })
  }

  function selectVariantPill(root, card, pill, defaultSellingPlan) {
    if (!card || !pill || pill.disabled) return

    applyVariantSelection(card, pill, defaultSellingPlan)
    showVariantQuantity(card, pill.getAttribute('data-variant-id'), lastKnownCart)
  }

  function variantFromDrawer(drawer) {
    const jsonEl = drawer.querySelector('[data-hook="product-json"]')
    if (!jsonEl) return null
    let product
    try {
      product = JSON.parse(jsonEl.textContent)
    } catch (e) {
      return null
    }
    const optionNames = product.options || []
    const selected = optionNames.map((name) => {
      const escaped = window.CSS && CSS.escape ? CSS.escape(name) : name
      const radio = drawer.querySelector(`input[type="radio"][data-hook="product-option"][name="${escaped}"]:checked`)
      if (radio) return radio.value
      const select = drawer.querySelector(`select[data-hook="product-option"][name="${escaped}"]`)
      return select ? select.value : ''
    })
    return (product.variants || []).find((variant) => {
      const values = [variant.option1, variant.option2, variant.option3].filter(
        (value) => value != null && value !== ''
      )
      return (
        values.length === selected.filter(Boolean).length && values.every((value, index) => value === selected[index])
      )
    })
  }

  function variantImageSrc(variant) {
    return variant?.featured_media?.src || variant?.featured_image?.src || ''
  }

  function syncDrawerVariantToCard(root, drawer, defaultSellingPlan) {
    const variant = variantFromDrawer(drawer)
    if (!variant) return

    const idInput = drawer.querySelector('input[name="id"]')
    if (idInput) idInput.value = String(variant.id)

    const src = variantImageSrc(variant)
    const drawerImg = drawer.querySelector('[data-hook="featured-image"] img')
    if (drawerImg && src) {
      drawerImg.src = src
      drawerImg.srcset = variantImageSrcset(src)
    }

    const card = getSubscribeCardFromDrawer(root, drawer)
    if (!card) return
    const pill = card.querySelector(`[data-hook="subscribe-variant-pill"][data-variant-id="${variant.id}"]`)
    if (pill && !pill.disabled) {
      selectVariantPill(root, card, pill, defaultSellingPlan)
      return
    }

    card.dataset.activeVariantId = String(variant.id)
    const hidden = card.querySelector('[data-hook="subscribe-variant-id"]')
    if (hidden) hidden.value = String(variant.id)
    if (src) updateCardImage(card, src)
    updateCardPrice(card, parseFloat(card.dataset.extraDiscountPercent) || 0)
    showVariantQuantity(card, variant.id, lastKnownCart)
  }

  function updateCardPrice(card, extraPercent) {
    const pricing = parseVariantPricing(card)
    const variantId = getSelectedVariantId(card)
    const variant = pricing.find((v) => String(v.id) === String(variantId)) || pricing[0]
    if (!variant) return

    const currentEl = card.querySelector('[data-hook="subscribe-current-price"]')
    const compareEl = card.querySelector('[data-hook="subscribe-compare-price"]')
    if (!currentEl) return

    const displayPrice = applyPercentOffCents(variant.baseSub, extraPercent)
    currentEl.textContent = formatMoney(displayPrice)

    if (compareEl) {
      const showCompare = variant.compare > displayPrice
      compareEl.textContent = formatMoney(variant.compare)
      compareEl.classList.toggle('hidden', !showCompare)
    }

    card.dataset.extraDiscountPercent = String(extraPercent || 0)
  }

  function isGiftCartLine(item) {
    const props = (item && item.properties) || {}
    return Boolean(
      props._subscription_gwp_free_gift ||
        props._subscription_free_gift ||
        props._single_subscription_free_gift ||
        props._Gifted
    )
  }

  function getSubscribeCardFromDrawer(root, el) {
    if (!root || !el) return null
    const fromCard = el.closest('[data-hook="subscribe-card"]')
    if (fromCard && root.contains(fromCard)) return fromCard

    const drawerState = el.closest('[data-hook="drawer-cart-state"]')
    const drawer = el.closest('[id^="bundle-drawer--"]')
    const productId = drawerState?.getAttribute('data-product-id') || drawer?.id?.replace(/^bundle-drawer--/, '') || ''
    if (!productId) return null
    return root.querySelector(`[data-hook="subscribe-card"][data-product-id="${productId}"]`)
  }

  function findSubscriptionLineForVariant(cart, card, variantId) {
    const wanted = String(variantId || '')
    if (!cart || !Array.isArray(cart.items) || !card || !wanted) return null

    const matches = cart.items.filter((item) => {
      if (!item || isGiftCartLine(item)) return false
      if (!(item.selling_plan_allocation || item.properties?._single_subscription)) return false
      if (String(item.variant_id) !== wanted) return false
      const productId = card.getAttribute('data-product-id')
      if (productId && String(item.product_id) !== String(productId)) return false
      return isDefaultSubscriptionLine(card, item)
    })

    return matches[0] || null
  }

  function findSubscriptionLineForCard(cart, card) {
    return findSubscriptionLineForVariant(cart, card, activeVariantId(card))
  }

  function activeVariantId(card) {
    return String(card?.dataset?.activeVariantId || getSelectedVariantId(card) || '')
  }

  const variantQtyCache = new WeakMap()

  function variantQtyMap(card) {
    let map = variantQtyCache.get(card)
    if (!map) {
      map = new Map()
      variantQtyCache.set(card, map)
    }
    return map
  }

  function rememberVariantQty(card, variantId, quantity) {
    const id = String(variantId || '')
    if (!card || !id) return
    variantQtyMap(card).set(id, Math.max(0, parseInt(quantity, 10) || 0))
  }

  function variantQtyInFlight(card, variantId) {
    return (
      String(card?.dataset?.qtyVariantId || '') === String(variantId || '') &&
      (card.dataset.qtyUpdating === 'true' || card.dataset.pendingQuantity != null)
    )
  }

  function quantityForVariant(card, variantId, cart) {
    const id = String(variantId || '')
    if (!id) return 0
    if (!variantQtyInFlight(card, id) && cart && Array.isArray(cart.items)) {
      const line = findSubscriptionLineForVariant(cart, card, id)
      rememberVariantQty(card, id, line ? parseInt(line.quantity, 10) || 0 : 0)
    }
    return variantQtyMap(card).get(id) || 0
  }

  function qtyLockMatches(card, variantId) {
    return card?.dataset?.qtyLock != null && String(card.dataset.qtyLockVariant || '') === String(variantId || '')
  }

  function showVariantQuantity(card, variantId, cart) {
    const wanted = String(variantId || '')
    if (!card || !wanted || activeVariantId(card) !== wanted) return
    const quantity = quantityForVariant(card, wanted, cart)
    if (qtyLockMatches(card, wanted) && String(quantity) !== card.dataset.qtyLock) return
    if (!variantQtyInFlight(card, wanted)) {
      card.dataset.confirmedQuantity = String(quantity)
      if (cart && Array.isArray(cart.items)) {
        const line = findSubscriptionLineForVariant(cart, card, wanted)
        if (line?.key) card.dataset.cartKey = line.key
        else delete card.dataset.cartKey
        delete card.dataset.pendingQuantity
      }
    }
    setCardQuantityUI(card, quantity, { immediateOff: quantity === 0 })
  }

  function setQuantityInputValue(input, quantity) {
    if (!input) return
    const next = String(Math.max(0, parseInt(quantity, 10) || 0))
    if (input.value !== next) input.value = next
  }

  function readQuantityValue(input) {
    if (!input) return '0'
    if (input.dataset.qtyCommit != null && input.dataset.qtyCommit !== '') return input.dataset.qtyCommit
    return input.value
  }

  function deferHiddenZero(card, input) {
    if (!input) return
    const write = () => {
      if (!input.isConnected || !card.isConnected || card.classList.contains('on')) return
      if ((parseInt(card.dataset.cartQuantity, 10) || 0) !== 0) return
      setQuantityInputValue(input, 0)
      delete input.dataset.qtyCommit
    }
    if (String(input.value) === '0') {
      delete input.dataset.qtyCommit
      return
    }
    requestAnimationFrame(write)
  }

  function syncOpenDrawerQuantity(card, quantity) {
    const productId = card.getAttribute('data-product-id')
    if (!productId) return
    const drawer = document.getElementById(`bundle-drawer--${productId}`)
    if (!drawer) return
    const input = drawer.querySelector(
      '.quantity-wrapper [data-hook="quantity"], [data-component="quantity-box"] [data-hook="quantity"]'
    )
    const qty = Math.max(0, parseInt(quantity, 10) || 0)
    const cartState = drawer.querySelector('[data-hook="drawer-cart-state"]')
    if (cartState) cartState.classList.toggle('on', qty > 0)
    if (qty > 0) setQuantityInputValue(input, qty)
    else if (input && String(input.value) !== '0') {
      requestAnimationFrame(() => {
        if (!input.isConnected || cartState?.classList.contains('on')) return
        setQuantityInputValue(input, 0)
      })
    }
  }

  function syncCartQuantityUI(card, quantity) {
    const cartKey = card.dataset.cartKey
    if (!cartKey) return

    document.querySelectorAll('[data-hook="cart-item"]').forEach((item) => {
      if (item.getAttribute('data-key') !== cartKey) return
      setQuantityInputValue(item.querySelector('[data-component="quantity-box"] [data-hook="quantity"]'), quantity)
    })
  }

  const QTY_ADD_GUARD_MS = 500

  function markQtyInteraction(card) {
    if (!card) return
    card.dataset.lastQtyInteractAt = String(Date.now())
  }

  function wasRecentQtyInteraction(card) {
    const last = parseInt(card?.dataset?.lastQtyInteractAt, 10) || 0
    return last > 0 && Date.now() - last < QTY_ADD_GUARD_MS
  }

  function setAddButtonEnabled(card, enabled) {
    const button = card.querySelector('[data-hook="subscribe-add"]')
    if (!button) return
    if (button.dataset.defaultDisabled == null) {
      button.dataset.defaultDisabled = button.disabled ? 'true' : 'false'
    }
    const canEnable = enabled && button.dataset.defaultDisabled !== 'true'
    button.disabled = !canEnable
    button.setAttribute('aria-hidden', canEnable ? 'false' : 'true')
  }

  function setCardQuantityUI(card, quantity, options = {}) {
    const qty = Math.max(0, parseInt(quantity, 10) || 0)
    const input = card.querySelector('[data-component="quantity-box"] [data-hook="quantity"]')
    card.dataset.cartQuantity = String(qty)

    if (qty > 0) {
      setQuantityInputValue(input, qty)
      card.classList.add('on')
      setAddButtonEnabled(card, false)
    } else {
      // Hide the stepper before the field can paint 0. Write 0 only after it is hidden.
      card.classList.remove('on')
      setAddButtonEnabled(card, true)
      deferHiddenZero(card, input)
    }

    if (options.syncDrawer !== false) syncOpenDrawerQuantity(card, qty)
    if (options.syncCart) syncCartQuantityUI(card, qty)
  }

  function syncCardsFromCart(root, cart) {
    root.querySelectorAll('[data-hook="subscribe-card"]').forEach((card) => {
      const active = activeVariantId(card)
      if (!card.dataset.activeVariantId && active) card.dataset.activeVariantId = active
      const pills = [...card.querySelectorAll('[data-hook="subscribe-variant-pill"]')]
      const ids = pills.length ? pills.map((pill) => pill.getAttribute('data-variant-id')) : [active]
      ids.forEach((id) => {
        if (!id || variantQtyInFlight(card, id)) return
        const line = findSubscriptionLineForVariant(cart, card, id)
        rememberVariantQty(card, id, line ? parseInt(line.quantity, 10) || 0 : 0)
      })
      if (variantQtyInFlight(card, active)) return
      const lockedHere = qtyLockMatches(card, active)
      const activeLinePreview = findSubscriptionLineForVariant(cart, card, active)
      const cartQty = activeLinePreview ? parseInt(activeLinePreview.quantity, 10) || 0 : 0
      if (lockedHere && String(cartQty) !== card.dataset.qtyLock) return
      if (lockedHere) {
        delete card.dataset.qtyLock
        delete card.dataset.qtyLockVariant
      }
      const activeLine = activeLinePreview
      if (activeLine?.key) card.dataset.cartKey = activeLine.key
      else delete card.dataset.cartKey
      showVariantQuantity(card, active, null)
    })
  }

  function applyCartItemQuantityToCards(root, itemEl, quantity) {
    if (!root || !itemEl) return
    const productId = String(itemEl.getAttribute('data-product-id') || '')
    const variantId = String(itemEl.getAttribute('data-variant-id') || '')
    const itemKey = itemEl.getAttribute('data-key') || ''
    const qty = Math.max(0, parseInt(quantity, 10) || 0)
    if (!productId) return

    if (!variantId) return

    root.querySelectorAll(`[data-hook="subscribe-card"][data-product-id="${productId}"]`).forEach((card) => {
      if (variantQtyInFlight(card, variantId)) return
      if (qtyLockMatches(card, variantId) && String(qty) !== card.dataset.qtyLock) return
      rememberVariantQty(card, variantId, qty)
      if (activeVariantId(card) !== variantId) return
      delete card.dataset.pendingQuantity
      if (itemKey) card.dataset.cartKey = itemKey
      else delete card.dataset.cartKey
      card.dataset.confirmedQuantity = String(qty)
      setCardQuantityUI(card, qty, { syncCart: false, immediateOff: qty === 0 })
    })
  }

  const liveRoots = new Set()

  function notifyCartChanged(cart) {
    liveRoots.forEach((root) => {
      if (!root.isConnected) {
        liveRoots.delete(root)
        return
      }
      if (cart && Array.isArray(cart.items)) {
        refreshFromCart(root, cart)
        return
      }
      fetchCart()
        .then((fresh) => refreshFromCart(root, fresh))
        .catch(() => {})
    })
  }

  function isCartMutationUrl(url) {
    return /\/cart\/(add|change|update|clear)/i.test(String(url || ''))
  }

  function handleCartMutationPayload(url, data) {
    if (suppressCartNetworkSync > 0) return
    const isFullCart = Boolean(data && data.token && Array.isArray(data.items) && typeof data.item_count === 'number')
    if (isFullCart && !/\/cart\/add/i.test(String(url || ''))) {
      notifyCartChanged(data)
      return
    }
    notifyCartChanged(null)
  }

  function bindCartNetworkSync() {
    if (window.__bundleDirectCartNetworkBound) return
    window.__bundleDirectCartNetworkBound = true

    const origFetch = window.fetch
    if (typeof origFetch === 'function') {
      window.fetch = function (input, init) {
        const url = typeof input === 'string' ? input : input && input.url
        const request = origFetch.apply(this, arguments)
        if (isCartMutationUrl(url)) {
          request
            .then((response) =>
              response
                .clone()
                .json()
                .catch(() => null)
            )
            .then((data) => handleCartMutationPayload(url, data))
            .catch(() => notifyCartChanged(null))
        }
        return request
      }
    }

    const origOpen = XMLHttpRequest.prototype.open
    const origSend = XMLHttpRequest.prototype.send
    XMLHttpRequest.prototype.open = function (method, url) {
      this.__bundleDirectCartUrl = url
      return origOpen.apply(this, arguments)
    }
    XMLHttpRequest.prototype.send = function () {
      if (isCartMutationUrl(this.__bundleDirectCartUrl)) {
        this.addEventListener('load', function () {
          if (this.status < 200 || this.status >= 300) return
          try {
            handleCartMutationPayload(this.__bundleDirectCartUrl, JSON.parse(this.responseText))
          } catch (error) {
            notifyCartChanged(null)
          }
        })
      }
      return origSend.apply(this, arguments)
    }
  }

  function bindCartClickSync() {
    if (window.__bundleDirectCartClickBound) return
    window.__bundleDirectCartClickBound = true

    document.addEventListener(
      'click',
      (event) => {
        const removeBtn = event.target.closest('[data-hook="remove-item"]')
        const increase = event.target.closest('[data-hook="increase-quantity"]')
        const decrease = event.target.closest('[data-hook="decrease-quantity"]')
        const control = removeBtn || increase || decrease
        if (!control) return
        const item = control.closest('[data-hook="cart-item"]')
        if (!item) return

        const input = item.querySelector('[data-hook="quantity"]')
        const current = Math.max(0, parseInt(input && input.value, 10) || 0)
        let next = current
        if (removeBtn) next = 0
        else if (increase) next = current + 1
        else next = Math.max(0, current - 1)

        liveRoots.forEach((root) => {
          if (root.isConnected) applyCartItemQuantityToCards(root, item, next)
        })
      },
      true
    )
  }

  function bindJqueryCartEvents() {
    const jq = window.jQuery || window.$
    if (!jq || typeof jq !== 'function' || !jq.fn || jq.__bundleDirectCartBound) return
    jq.__bundleDirectCartBound = true
    jq(document).on(
      'cart:update.bundleBuilderDirect cart:updated.bundleBuilderDirect cart:forceRender.bundleBuilderDirect',
      (_event, payload) => {
        notifyCartChanged(payload && payload.cart)
      }
    )
  }

  function cartItemCount(cart) {
    if (!cart || typeof cart !== 'object') return null
    if (typeof cart.item_count === 'number') return cart.item_count
    if (!Array.isArray(cart.items)) return null
    return cart.items.reduce((sum, item) => sum + (parseInt(item.quantity, 10) || 0), 0)
  }

  function shouldApplyCartSnapshot(cart) {
    const api = window.__lilacCartCache
    if (api && typeof api.shouldApplyCartSnapshot === 'function') return api.shouldApplyCartSnapshot(cart)
    const count = cartItemCount(cart)
    if (count == null) return false
    const shown = window.__lilacHeaderCartCount || 0
    if (count === 0 && shown > 0 && !cart.__cartMutation) {
      const fetchedAt = cart.__fetchedAt || 0
      const shownAt = window.__lilacHeaderCartAt || 0
      if (!fetchedAt || fetchedAt < shownAt) return false
    }
    return true
  }

  function rememberHeaderCartCount(cart) {
    const api = window.__lilacCartCache
    if (api && typeof api.rememberHeaderCartCount === 'function') return api.rememberHeaderCartCount(cart)
    const count = cartItemCount(cart)
    if (count == null) return null
    window.__lilacHeaderCartCount = count
    if (count > 0) window.__lilacHeaderCartAt = Date.now()
    return count
  }

  function syncHeaderCartDot(cart) {
    if (!shouldApplyCartSnapshot(cart)) return
    const count = rememberHeaderCartCount(cart)
    if (count == null) return
    if (cart.token && window.__lilacCartCache && typeof window.__lilacCartCache.setCart === 'function') {
      window.__lilacCartCache.setCart(cart)
    }
    document.querySelectorAll('[data-hook="cart-count"]').forEach((dot) => {
      dot.classList.toggle('on', count > 0)
    })
  }

  async function refreshFromCart(root, cart) {
    if (cart && cart.__revision != null && cart.__revision !== cartRevision) return false
    if (!shouldApplyCartSnapshot(cart)) return false
    const refreshSequence = (root.__bundleDirectRefreshSequence || 0) + 1
    root.__bundleDirectRefreshSequence = refreshSequence
    if (cart && Array.isArray(cart.items)) lastKnownCart = cart
    syncHeaderCartDot(cart)
    const config = getRewardConfig(root)
    const totals = await getSubscriptionTotals(root, cart)
    // Product selling-plan lookups are asynchronous. Ignore older cart reads
    // if another cart event arrived while those lookups were in flight.
    if (root.__bundleDirectRefreshSequence !== refreshSequence) return false
    const activeTiers = getActiveTiers(
      config?.thresholds,
      totals.displayTotal,
      totals.thresholdTotal,
      cart?.items_subtotal_price || 0,
      config?.shippingThreshold || 0
    )
    const extraPercent = getExtraDiscountPercent(activeTiers, config)
    root.querySelectorAll('[data-hook="subscribe-card"]').forEach((card) => {
      updateCardPrice(card, extraPercent)
    })
    syncCardsFromCart(root, cart)
    await updateRewardWidgets(root, cart, config, totals)
    if (root.__bundleDirectRefreshSequence !== refreshSequence) return false
    return notifyRewardThreshold(root, config, activeTiers, cart, totals)
  }

  function rewardProgressTotal(totals, tierIndex) {
    const { displayTotal, thresholdTotal } = totals
    return tierIndex >= 2 ? thresholdTotal : displayTotal
  }

  function notifyRewardThreshold(root, config, activeTiers, cart, totals) {
    const previousRaw = root.dataset.rewardActiveTier
    root.dataset.rewardActiveTier = String(activeTiers)
    if (previousRaw == null || previousRaw === '') return false
    const previous = parseInt(previousRaw, 10) || 0
    if (activeTiers <= previous) return false

    const unlockMessage = config?.toastMessages?.[activeTiers - 1] || ''
    const nextTemplate = config?.nextToastMessages?.[activeTiers - 1] || ''
    const nextThreshold = config?.thresholds?.[activeTiers] || 0
    let showed = false

    const hasNext = Boolean(nextTemplate && nextThreshold > 0)
    if (unlockMessage) {
      showSubscribeToast(unlockMessage, hasNext ? 2000 : 4000)
      showed = true
    }
    if (hasNext) {
      const remaining = Math.max(0, nextThreshold - rewardProgressTotal(totals, activeTiers))
      showSubscribeToast(nextTemplate, 4000, remaining)
      showed = true
    }
    if (showed) root.dataset.rewardThresholdToast = '1'
    return showed
  }

  function cartRootUrl(path) {
    const rootPath = window.Shopify?.routes?.root || '/'
    return `${rootPath}${path.replace(/^\//, '')}`
  }

  function addItemsToCart(items) {
    const revision = ++cartRevision
    suppressCartNetworkSync++
    return fetch(cartRootUrl('cart/add.js'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ items }),
      credentials: 'same-origin',
    })
      .then(async (response) => {
        const data = await response.json().catch(() => ({}))
        if (!response.ok) {
          const message = data.description || data.message || 'Unable to add to cart'
          throw new Error(message)
        }
        if (data && typeof data === 'object') {
          data.__revision = revision
          data.__cartMutation = true
        }
        return data
      })
      .finally(() => {
        suppressCartNetworkSync = Math.max(0, suppressCartNetworkSync - 1)
      })
  }

  function changeCartLine(id, quantity) {
    if (!id) {
      return fetchCart()
    }
    const revision = ++cartRevision
    suppressCartNetworkSync++
    return fetch(cartRootUrl('cart/change.js'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ id, quantity: Math.max(0, Number(quantity) || 0) }),
      credentials: 'same-origin',
    })
      .then(async (response) => {
        const data = await response.json().catch(() => ({}))
        if (!response.ok) {
          if (quantity <= 0) {
            return fetchCart()
          }
          const message = data.description || data.message || 'Unable to update cart'
          const error = new Error(message)
          error.cartChangeFailed = true
          throw error
        }
        if (data && typeof data === 'object') {
          data.__revision = revision
          data.__cartMutation = true
        }
        return data
      })
      .finally(() => {
        suppressCartNetworkSync = Math.max(0, suppressCartNetworkSync - 1)
      })
  }

  /**
   * Same +/- behavior as snippets/quantity-box + quantity-box.js.
   * Capture-phase handlers win over the global component to avoid double increments.
   */
  function bindQuantityBoxes(root) {
    if (root.dataset.directQtyBound === 'true') return
    root.dataset.directQtyBound = 'true'

    function getCardQtyButton(event) {
      const increase = event.target.closest('[data-hook="increase-quantity"]')
      const decrease = event.target.closest('[data-hook="decrease-quantity"]')
      const button = increase || decrease
      if (!button || !root.contains(button)) {
        return { increase: null, decrease: null, button: null, card: null }
      }

      const inCard = button.closest('[data-hook="subscribe-card"]')
      const inDrawer = button.closest('[data-hook="drawer-cart-state"]')
      if (!inCard && !inDrawer) {
        return { increase: null, decrease: null, button: null, card: null }
      }

      return {
        increase,
        decrease,
        button,
        card: getSubscribeCardFromDrawer(root, button),
      }
    }

    let qtyGestureCard = null

    function endQtyGesture(card) {
      if (!card || card.dataset.qtyGesture !== '1') return
      window.setTimeout(() => {
        delete card.dataset.qtyGesture
        const qty = parseInt(card.dataset.cartQuantity, 10) || 0
        if (qty > 0) return
        card.classList.remove('on')
        setAddButtonEnabled(card, true)
      }, 0)
    }

    root.addEventListener(
      'pointerdown',
      (event) => {
        const { button, card } = getCardQtyButton(event)
        if (!button || !card) return
        markQtyInteraction(card)
        card.dataset.qtyGesture = '1'
        qtyGestureCard = card
      },
      true
    )

    window.addEventListener('pointerup', () => {
      if (!qtyGestureCard) return
      const card = qtyGestureCard
      qtyGestureCard = null
      endQtyGesture(card)
    })

    window.addEventListener('pointercancel', () => {
      if (!qtyGestureCard) return
      const card = qtyGestureCard
      qtyGestureCard = null
      endQtyGesture(card)
    })

    root.addEventListener(
      'click',
      (event) => {
        const { increase, decrease, button, card } = getCardQtyButton(event)
        if (!button || !card) return

        markQtyInteraction(card)

        const input = card.querySelector('[data-component="quantity-box"] [data-hook="quantity"]')
        if (!input) return

        event.preventDefault()
        event.stopImmediatePropagation()

        const current = parseInt(card.dataset.pendingQuantity ?? card.dataset.cartQuantity ?? input.value, 10) || 0
        let next
        if (increase) {
          const max = input.max !== '' ? parseInt(input.max, 10) : Infinity
          next = Math.min(current + 1, Number.isNaN(max) ? current + 1 : max)
        } else {
          const min = input.min !== '' ? parseInt(input.min, 10) : 0
          next = Math.max(current - 1, Number.isNaN(min) ? 0 : min)
        }

        if (next <= 0) {
          input.dataset.qtyCommit = '0'
          card.classList.remove('on')
          setAddButtonEnabled(card, true)
        } else {
          delete input.dataset.qtyCommit
          input.value = String(next)
        }

        if (window.jQuery) {
          window.jQuery(input).trigger('change')
        } else {
          input.dispatchEvent(new Event('change', { bubbles: true }))
        }
      },
      true
    )
  }

  function refreshCartBadge(cart, sectionHtml) {
    syncHeaderCartDot(cart)
    const options = {}
    const hasCart = Boolean(cart && cart.token && Array.isArray(cart.items))
    if (hasCart) options.cart = cart
    else options.invalidateCart = true
    if (sectionHtml) options.sectionHtml = sectionHtml
    if (window.jQuery) {
      window.jQuery(document).trigger('cart:forceRender', [options])
    } else {
      document.dispatchEvent(new CustomEvent('cart:forceRender', { detail: options }))
    }
  }

  function prefetchCartSection() {
    const sectionId = document.querySelector('#slide-cart')?.getAttribute('data-section-id')
    if (!sectionId) return Promise.resolve(null)
    return fetch(`${cartRootUrl('cart')}?sections=${encodeURIComponent(sectionId)}`, {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: '*/*' },
    })
      .then((response) => (response.ok ? response.json() : null))
      .then((json) => json?.[sectionId] || null)
      .catch(() => null)
  }

  function releaseDrawerAddLoading() {
    document.querySelectorAll('[data-hook="bundle-add"].loading').forEach((button) => {
      button.classList.remove('loading')
    })
  }

  function formatToastPrice(cents) {
    return `${(Math.max(0, Number(cents) || 0) / 100).toFixed(2)}$`
  }

  const toastQueue = []
  let toastActive = false

  function showSubscribeToast(message, duration = 4000, priceCents) {
    toastQueue.push({ message, duration, priceCents })
    if (!toastActive) playNextToast()
  }

  function playNextToast() {
    const next = toastQueue.shift()
    if (!next) {
      toastActive = false
      return
    }
    toastActive = true
    renderSubscribeToast(next.message, next.duration, next.priceCents, () => {
      toastActive = false
      playNextToast()
    })
  }

  function renderSubscribeToast(message, duration, priceCents, onDone) {
    let stack = document.getElementById('subscribe-direct-toasts')
    if (!stack) {
      stack = document.createElement('div')
      stack.id = 'subscribe-direct-toasts'
      stack.className = 'bundler-direct-toasts'
      document.body.appendChild(stack)
    }

    const toast = document.createElement('div')
    toast.className = 'bundler-direct-toast'
    toast.setAttribute('role', 'status')
    toast.setAttribute('aria-live', 'polite')

    const messageText = document.createElement('span')
    messageText.className = 'bundler-direct-toast__message'
    String(message || '')
      .split('{price}')
      .forEach((part, index, parts) => {
        messageText.appendChild(document.createTextNode(part))
        if (index >= parts.length - 1) return
        const price = document.createElement('strong')
        price.textContent = formatToastPrice(priceCents)
        messageText.appendChild(price)
      })

    const closeBtn = document.createElement('button')
    closeBtn.type = 'button'
    closeBtn.className = 'bundler-direct-toast__close'
    closeBtn.setAttribute('aria-label', 'Close notification')
    closeBtn.textContent = '×'

    toast.appendChild(messageText)
    toast.appendChild(closeBtn)

    let timeoutId = null
    let closed = false
    const closeToast = () => {
      if (closed) return
      closed = true
      if (timeoutId) clearTimeout(timeoutId)
      toast.classList.remove('is-visible')
      setTimeout(() => {
        toast.remove()
        if (stack && !stack.childElementCount) stack.remove()
        if (typeof onDone === 'function') onDone()
      }, 300)
    }
    closeBtn.addEventListener('click', closeToast)
    stack.appendChild(toast)
    requestAnimationFrame(() => {
      toast.classList.add('is-visible')
      timeoutId = setTimeout(closeToast, duration)
    })
  }

  function fetchCart() {
    const revision = cartRevision
    const fetchedAt = Date.now()
    return fetch(cartRootUrl('cart.js'), { credentials: 'same-origin', cache: 'no-store' })
      .then((r) => r.json())
      .then((cart) => {
        if (cart && typeof cart === 'object') {
          cart.__revision = revision
          cart.__fetchedAt = fetchedAt
        }
        return cart
      })
  }

  function setBusy(button, busy, label) {
    if (!button) return
    button.disabled = Boolean(busy)
    if (label) button.textContent = label
  }

  function setCardInteractionLocked(card, locked) {
    if (!card) return
    card.classList.toggle('is-adding', locked)
    if (locked) {
      card.setAttribute('aria-busy', 'true')
      card.setAttribute('inert', '')
      return
    }
    card.removeAttribute('aria-busy')
    card.removeAttribute('inert')
  }

  function buildSubscriptionItem(variantId, quantity, sellingPlanId) {
    return {
      id: Number(variantId),
      quantity: Number(quantity) || 1,
      selling_plan: Number(sellingPlanId),
      properties: {
        _single_subscription: 'true',
      },
    }
  }

  function updateCardQuantityInCart(root, card, nextQuantity, defaultSellingPlan) {
    const quantity = Math.max(0, parseInt(nextQuantity, 10) || 0)
    const variantId = getSelectedVariantId(card)
    const sellingPlan = getCardDefaultSellingPlan(card, variantId, defaultSellingPlan)
    const title = card.getAttribute('data-title') || 'Item'
    const selectedVariantId = String(variantId)
    let cartKey = card.dataset.cartKey
    if (cartKey && lastKnownCart) {
      const keyedLine = (lastKnownCart.items || []).find((item) => item.key === cartKey)
      if (
        keyedLine &&
        (String(keyedLine.variant_id) !== selectedVariantId ||
          !isDefaultSubscriptionLine(card, keyedLine, defaultSellingPlan))
      ) {
        cartKey = ''
        delete card.dataset.cartKey
      }
    }
    if (!cartKey) {
      const existing = findSubscriptionLineForCard(lastKnownCart, card)
      if (existing?.key) cartKey = existing.key
    }

    if (!variantId) {
      return Promise.reject(new Error('Missing variant'))
    }

    const syncAfter = (cart, pendingSection) => {
      refreshFromCart(root, cart)
      const openDrawer = (sectionHtml) => {
        if (root.dataset.rewardThresholdToast === '1') delete root.dataset.rewardThresholdToast
        refreshCartBadge(cart, sectionHtml)
      }
      if (pendingSection && typeof pendingSection.then === 'function') {
        pendingSection.then(openDrawer)
      } else {
        openDrawer(null)
      }
      const line = findSubscriptionLineForVariant(cart, card, variantId)
      const confirmed = line ? parseInt(line.quantity, 10) || 0 : quantity
      rememberVariantQty(card, variantId, confirmed)
      if (String(activeVariantId(card)) === selectedVariantId) {
        if (line?.key) card.dataset.cartKey = line.key
        else delete card.dataset.cartKey
        card.dataset.confirmedQuantity = String(confirmed)
      }
      return cart
    }

    const changeExistingLine = (cartKey) =>
      changeCartLine(cartKey, quantity)
        .then((cart) => {
          if (!cart || !Array.isArray(cart.items)) return fetchCart()
          return cart
        })
        .catch(async (error) => {
          const cart = await fetchCart().catch(() => lastKnownCart)
          const line = findSubscriptionLineForVariant(cart, card, variantId)
          if (quantity <= 0) {
            if (line?.key && line.key !== cartKey) {
              return changeCartLine(line.key, 0).then((fresh) =>
                fresh && Array.isArray(fresh.items) ? fresh : fetchCart()
              )
            }
            return cart
          }
          if (line?.key && String(line.variant_id) === String(variantId)) {
            return changeCartLine(line.key, quantity)
          }
          throw error
        })

    if (quantity <= 0) {
      if (!cartKey) {
        setCardQuantityUI(card, 0, { immediateOff: true })
        return fetchCart().then(syncAfter)
      }
      return changeExistingLine(cartKey).then(syncAfter)
    }

    if (cartKey) {
      return changeExistingLine(cartKey).then((cart) => {
        const line = findSubscriptionLineForVariant(cart, card, variantId)
        if (line && String(line.variant_id) === String(variantId)) {
          return syncAfter(cart)
        }
        if (!sellingPlan) {
          throw new Error('This product is missing a 30-day subscription plan.')
        }
        return finishSubscriptionAdd(title)
      })
    }

    if (!sellingPlan) {
      return Promise.reject(new Error('This product is missing a 30-day subscription plan.'))
    }

    return finishSubscriptionAdd(title)

    function finishSubscriptionAdd() {
      return addItemsToCart([buildSubscriptionItem(variantId, quantity, sellingPlan)]).then((added) => {
        releaseDrawerAddLoading()
        const pendingSection = prefetchCartSection()
        return fetchCart()
          .then((cart) => syncAfter(cart, pendingSection))
          .catch(() => added)
      })
    }
  }

  class BundleBuilderDirect {
    constructor(root) {
      this.root = root
      this.defaultSellingPlan = parseInt(root.getAttribute('data-selling-plan'), 10) || 4124213441
    }

    static mount(root) {
      if (!root || root.dataset.directInit === 'true') return
      const instance = new BundleBuilderDirect(root)
      instance.init()
    }

    init() {
      const { root } = this
      root.dataset.directInit = 'true'
      bindQuantityBoxes(root)
      this.bindRootClick()
      this.bindDrawerVariantSync()
      this.bindRootChange()
      liveRoots.add(root)
      bindCartNetworkSync()
      bindCartClickSync()
      bindJqueryCartEvents()
      setTimeout(bindJqueryCartEvents, 0)
      setTimeout(bindJqueryCartEvents, 500)
      this.bindLiveCards()
      this.bindCartItemQuantitySync()
      this.bindJqueryQuantityChange()
      this.bindPillEdgeFade()
      this.bindRewardRails()
      this.bindCollectionFilters()
      fetchCart()
        .then((cart) => refreshFromCart(root, cart))
        .catch(() => {})
      this.loadStampedBadges()
    }

    bindPillEdgeFade() {
      const rows = this.root.querySelectorAll('[data-hook="subscribe-variant-pills"]')
      if (!rows.length) return

      const update = (row) => {
        if (!row.isConnected) return
        const overflows = row.scrollWidth - row.clientWidth > 1
        const atStart = row.scrollLeft <= 1
        const atEnd = row.scrollLeft + row.clientWidth >= row.scrollWidth - 1
        row.classList.toggle('is-overflowing', overflows)
        row.classList.toggle('is-scrolled', overflows && !atStart)
        row.classList.toggle('is-scrolled-end', overflows && atEnd)
      }

      this.updatePillEdgeFade = () => rows.forEach(update)

      rows.forEach((row) => {
        update(row)
        row.addEventListener('scroll', () => update(row), { passive: true })
      })

      window.addEventListener('resize', this.updatePillEdgeFade)
      if (window.ResizeObserver) {
        const observer = new ResizeObserver(() => this.updatePillEdgeFade())
        rows.forEach((row) => observer.observe(row))
      }
    }

    bindRewardRails() {
      const place = () => placeMobileRewardRails(this.root)
      place()
      requestAnimationFrame(place)
      if (window.ResizeObserver) {
        const observer = new ResizeObserver(place)
        this.root.querySelectorAll('[data-subscription-reward-layout="mobile"]').forEach((layout) => {
          observer.observe(layout)
        })
      } else {
        window.addEventListener('resize', place)
      }
    }

    flushCardQuantity(card) {
      if (!card || card.dataset.qtyUpdating === 'true') return
      if (card.dataset.pendingQuantity == null) return

      const nextQuantity = Math.max(0, parseInt(card.dataset.pendingQuantity, 10) || 0)
      const confirmedQuantity = parseInt(card.dataset.confirmedQuantity, 10)
      delete card.dataset.pendingQuantity

      if (!Number.isNaN(confirmedQuantity) && nextQuantity === confirmedQuantity) {
        setCardQuantityUI(card, nextQuantity, {
          syncCart: true,
          immediateOff: nextQuantity === 0,
        })
        return
      }

      const requestId = (parseInt(card.dataset.qtyRequestId, 10) || 0) + 1
      const variantId = activeVariantId(card)
      card.dataset.qtyRequestId = String(requestId)
      card.dataset.qtyVariantId = variantId
      card.dataset.qtyUpdating = 'true'
      rememberVariantQty(card, variantId, nextQuantity)
      updateCardQuantityInCart(this.root, card, nextQuantity, this.defaultSellingPlan)
        .then((cart) => {
          if (card.dataset.qtyRequestId !== String(requestId)) return
          const line = findSubscriptionLineForVariant(cart, card, variantId)
          rememberVariantQty(card, variantId, line ? parseInt(line.quantity, 10) || 0 : nextQuantity)
          if (activeVariantId(card) !== variantId) {
            showVariantQuantity(card, activeVariantId(card), cart)
            return
          }
          showVariantQuantity(card, variantId, null)
        })
        .catch((error) => {
          if (card.dataset.qtyRequestId !== String(requestId)) return
          console.error('[bundle-builder-direct] Quantity update failed', error)
          const fallback = quantityForVariant(card, variantId, lastKnownCart)
          rememberVariantQty(card, variantId, fallback)
          if (activeVariantId(card) === variantId) {
            setCardQuantityUI(card, fallback, {
              syncCart: true,
              immediateOff: fallback === 0,
            })
          } else {
            showVariantQuantity(card, activeVariantId(card), lastKnownCart)
          }
          window.alert(error.message || 'Unable to update quantity')
        })
        .finally(() => {
          if (card.dataset.qtyRequestId !== String(requestId)) return
          delete card.dataset.qtyUpdating
          if (card.dataset.pendingQuantity != null) this.flushCardQuantity(card)
        })
    }

    handleCardQuantityChange(card, rawQuantity) {
      if (!card) return
      markQtyInteraction(card)
      const nextQuantity = Math.max(0, parseInt(rawQuantity, 10) || 0)
      const displayedQuantity = parseInt(card.dataset.cartQuantity, 10) || 0
      const pendingQuantity = parseInt(card.dataset.pendingQuantity, 10)

      if (nextQuantity === displayedQuantity) {
        if (card.dataset.qtyUpdating === 'true') return
        if (card.dataset.pendingQuantity == null) return
        if (!Number.isNaN(pendingQuantity) && pendingQuantity === nextQuantity) return
      }

      if (card.dataset.confirmedQuantity == null) {
        card.dataset.confirmedQuantity = String(displayedQuantity)
      }
      card.dataset.pendingQuantity = String(nextQuantity)
      card.dataset.qtyLock = String(nextQuantity)
      card.dataset.qtyLockVariant = activeVariantId(card)
      rememberVariantQty(card, activeVariantId(card), nextQuantity)
      setCardQuantityUI(card, nextQuantity, { syncCart: true, immediateOff: nextQuantity === 0 })
      this.flushCardQuantity(card)
    }

    startCardSubscriptionAdd(card, subscribeBtn, drawerAddBtn) {
      if (!card) return
      const cartQuantity = parseInt(card.dataset.cartQuantity, 10) || 0
      const confirmedQuantity = parseInt(card.dataset.confirmedQuantity, 10) || 0
      if (
        card.dataset.qtyGesture === '1' ||
        (subscribeBtn && subscribeBtn.disabled) ||
        card.dataset.qtyUpdating === 'true' ||
        card.dataset.pendingQuantity != null ||
        cartQuantity > 0 ||
        confirmedQuantity > 0 ||
        wasRecentQtyInteraction(card)
      ) {
        return
      }

      const variantId = activeVariantId(card)
      const sellingPlan = getCardDefaultSellingPlan(card, variantId, this.defaultSellingPlan)
      if (!variantId) return
      if (!sellingPlan) {
        window.alert('This product is missing a 30-day subscription plan.')
        return
      }

      let originalLabel = subscribeBtn?.textContent?.trim() || 'Subscribe'
      if (/^adding/i.test(originalLabel)) originalLabel = 'Subscribe'
      if (subscribeBtn) subscribeBtn.textContent = originalLabel
      const requestId = (parseInt(card.dataset.qtyRequestId, 10) || 0) + 1
      card.dataset.qtyRequestId = String(requestId)
      card.dataset.qtyVariantId = String(variantId)
      card.dataset.confirmedQuantity = '0'
      card.dataset.qtyUpdating = 'true'
      rememberVariantQty(card, variantId, 1)
      setCardQuantityUI(card, 1, { syncCart: true })

      updateCardQuantityInCart(this.root, card, 1, this.defaultSellingPlan)
        .then((cart) => {
          if (card.dataset.qtyRequestId !== String(requestId)) return
          const line = findSubscriptionLineForVariant(cart, card, variantId)
          const confirmed = line ? parseInt(line.quantity, 10) || 0 : 1
          rememberVariantQty(card, variantId, confirmed)
          if (activeVariantId(card) !== String(variantId)) {
            showVariantQuantity(card, activeVariantId(card), cart)
            return
          }
          card.dataset.confirmedQuantity = String(confirmed)
          setCardQuantityUI(card, confirmed, { syncCart: true })
        })
        .catch((error) => {
          if (card.dataset.qtyRequestId !== String(requestId)) return
          console.error('[bundle-builder-direct] Add to box failed', error)
          rememberVariantQty(card, variantId, 0)
          if (activeVariantId(card) === String(variantId)) {
            card.dataset.confirmedQuantity = '0'
            setCardQuantityUI(card, 0, { syncCart: true, immediateOff: true })
          } else {
            showVariantQuantity(card, activeVariantId(card), lastKnownCart)
          }
          window.alert(error.message || 'Unable to add to box')
        })
        .finally(() => {
          const stale = card.dataset.qtyRequestId !== String(requestId)
          if (!stale) {
            delete card.dataset.qtyUpdating
            if (card.dataset.pendingQuantity != null) this.flushCardQuantity(card)
            if (subscribeBtn && /^adding/i.test(subscribeBtn.textContent.trim())) {
              subscribeBtn.textContent = originalLabel
            }
            if (drawerAddBtn) drawerAddBtn.classList.remove('loading')
            if (activeVariantId(card) === String(variantId)) {
              const qty = parseInt(card.dataset.cartQuantity, 10) || 0
              setAddButtonEnabled(card, qty === 0 && !card.classList.contains('on'))
            }
          }
          if (card.dataset.qtyUpdating !== 'true') setCardInteractionLocked(card, false)
        })
    }

    bindDrawerVariantSync() {
      const { root, defaultSellingPlan } = this
      root.addEventListener('change', (event) => {
        const option = event.target.closest('[data-hook="product-option"]')
        if (!option || !root.contains(option)) return
        const drawer = option.closest('[id^="bundle-drawer--"]')
        if (!drawer) return
        syncDrawerVariantToCard(root, drawer, defaultSellingPlan)
      })
    }

    bindRootClick() {
      const { root, defaultSellingPlan } = this
      root.addEventListener('click', (event) => {
        const gridToggle = event.target.closest(
          '[data-hook="bundler-direct-expand"], [data-hook="bundler-direct-collapse"]'
        )
        if (gridToggle && root.contains(gridToggle)) {
          event.preventDefault()
          const grid = gridToggle.closest('.bundler-direct-collection')
          if (!grid) return
          const expanded = gridToggle.getAttribute('data-hook') === 'bundler-direct-expand'
          grid.classList.toggle('is-expanded', expanded)
          const shopAll = grid.querySelector('[data-hook="bundler-direct-expand"]')
          const shopLess = grid.querySelector('[data-hook="bundler-direct-collapse"]')
          if (shopAll) shopAll.hidden = expanded
          if (shopLess) shopLess.hidden = !expanded
          if (expanded && typeof this.updatePillEdgeFade === 'function') {
            requestAnimationFrame(() => this.updatePillEdgeFade())
          }
          if (!expanded && shopAll) {
            const styles = getComputedStyle(document.documentElement)
            const headerOffset = parseFloat(styles.getPropertyValue('--bundler-header-offset')) || 0
            const filtersOffset = parseFloat(styles.getPropertyValue('--bundler-filters-height')) || 0
            const top = shopAll.getBoundingClientRect().top + window.scrollY - headerOffset - filtersOffset
            window.scrollTo({ top: Math.max(0, top), behavior: 'auto' })
          }
          return
        }

        const variantPill = event.target.closest('[data-hook="subscribe-variant-pill"]')
        if (variantPill && root.contains(variantPill)) {
          event.preventDefault()
          event.stopPropagation()
          const card = variantPill.closest('[data-hook="subscribe-card"]')
          if (!card) return
          selectVariantPill(root, card, variantPill, defaultSellingPlan)
          return
        }

        const subscribeBtn = event.target.closest('[data-hook="subscribe-add"]')
        const drawerAddBtn = event.target.closest('[data-hook="bundle-add"]')
        if ((subscribeBtn || drawerAddBtn) && root.contains(subscribeBtn || drawerAddBtn)) {
          event.preventDefault()
          event.stopPropagation()
          const source = subscribeBtn || drawerAddBtn
          const card = getSubscribeCardFromDrawer(root, source)
          if (!card) return
          this.startCardSubscriptionAdd(card, subscribeBtn, drawerAddBtn)
          return
        }

        const presetBtn = event.target.closest('[data-hook="subscribe-preset"]')
        if (presetBtn && root.contains(presetBtn)) {
          event.preventDefault()
          let items = []
          try {
            items = JSON.parse(presetBtn.getAttribute('data-subscribe-items') || '[]')
          } catch (e) {
            items = []
          }
          const fallbackPlan = parseInt(presetBtn.getAttribute('data-selling-plan'), 10) || defaultSellingPlan
          const payload = items
            .map((item) => {
              const planId = sellingPlanForVariant(root, item.id, fallbackPlan)
              if (!item.id || !planId) return null
              return buildSubscriptionItem(item.id, item.quantity, planId)
            })
            .filter(Boolean)

          if (!payload.length) return

          const originalLabel = presetBtn.textContent
          setBusy(presetBtn, true, 'Adding…')

          addItemsToCart(payload)
            .then(() => {
              const pendingSection = prefetchCartSection()
              return fetchCart().then((cart) => {
                refreshFromCart(root, cart)
                pendingSection.then((sectionHtml) => {
                  if (root.dataset.rewardThresholdToast === '1') delete root.dataset.rewardThresholdToast
                  refreshCartBadge(cart, sectionHtml)
                })
              })
            })
            .catch((error) => {
              console.error('[bundle-builder-direct] Preset subscribe failed', error)
              window.alert(error.message || 'Unable to subscribe')
            })
            .finally(() => setBusy(presetBtn, false, originalLabel))
        }
      })
    }

    bindRootChange() {
      const { root, defaultSellingPlan } = this
      root.addEventListener('change', (event) => {
        const quantityInput = event.target.closest('[data-component="quantity-box"] [data-hook="quantity"]')
        if (quantityInput && root.contains(quantityInput)) {
          const card = getSubscribeCardFromDrawer(root, quantityInput)
          if (!card) return
          const source = card.contains(quantityInput)
            ? quantityInput
            : card.querySelector('[data-component="quantity-box"] [data-hook="quantity"]') || quantityInput
          this.handleCardQuantityChange(card, readQuantityValue(source))
          return
        }

        const select = event.target.closest('[data-hook="subscribe-variant"]')
        if (!select || !root.contains(select)) return
        const card = select.closest('[data-hook="subscribe-card"]')
        if (!card) return
        const extra = parseFloat(card.dataset.extraDiscountPercent) || 0
        const variantId = getSelectedVariantId(card)
        const planId = getVariantSellingPlan(card, variantId, defaultSellingPlan)
        if (planId) card.setAttribute('data-selling-plan', String(planId))
        updateCardPrice(card, extra)
      })
    }

    bindLiveCards() {
      this.root.querySelectorAll('[data-hook="subscribe-card"]').forEach((card) => {
        if (!card.dataset.activeVariantId) {
          const initialVariant = getSelectedVariantId(card)
          if (initialVariant) card.dataset.activeVariantId = String(initialVariant)
        }
        const selectedPill = card.querySelector('[data-hook="subscribe-variant-pill"][aria-selected="true"]')
        if (selectedPill) updateCardImage(card, selectedPill.getAttribute('data-image'))
        if (card.dataset.confirmedQuantity == null) {
          card.dataset.confirmedQuantity = card.dataset.cartQuantity || '0'
        }
        const initialVariant = activeVariantId(card)
        if (initialVariant) rememberVariantQty(card, initialVariant, card.dataset.cartQuantity || 0)
        const addBtn = card.querySelector('[data-hook="subscribe-add"]')
        if (addBtn && addBtn.dataset.defaultDisabled == null) {
          addBtn.dataset.defaultDisabled = addBtn.disabled ? 'true' : 'false'
        }
        setAddButtonEnabled(card, (parseInt(card.dataset.cartQuantity, 10) || 0) === 0)
      })
    }

    applyLiveCartQuantity(input) {
      const item = input?.closest('[data-hook="cart-item"]')
      if (!item) return
      applyCartItemQuantityToCards(this.root, item, input.value)
    }

    bindCartItemQuantitySync() {
      document.addEventListener(
        'change',
        (event) => {
          const input = event.target?.closest?.('[data-hook="quantity"]')
          if (!input || !input.closest('[data-hook="cart-item"]')) return
          this.applyLiveCartQuantity(input)
        },
        true
      )
    }

    bindJqueryQuantityChange() {
      if (!window.jQuery) return
      const { root } = this
      const builder = this
      window.jQuery(root).on('quantity-box:change.bundleBuilderDirect', '[data-hook="quantity"]', function () {
        const card = getSubscribeCardFromDrawer(root, this)
        if (!card || !root.contains(card)) return
        const source = card.contains(this)
          ? this
          : card.querySelector('[data-component="quantity-box"] [data-hook="quantity"]') || this
        builder.handleCardQuantityChange(card, readQuantityValue(source))
      })
    }

    bindCollectionFilters() {
      const form = this.root.querySelector('#collection-filters [data-hook="filter-form"]')
      if (!form || form.dataset.filterBound === 'true') return
      form.dataset.filterBound = 'true'
      this.filterGridHtml = new WeakMap()

      const filtersRoot = form.closest('#collection-filters')
      const defaultSort = filtersRoot?.getAttribute('data-sort') || 'manual'

      const readParams = () => {
        const params = new URLSearchParams()
        new FormData(form).forEach((value, key) => {
          const text = String(value ?? '').trim()
          if (text) params.append(key, text)
        })
        return params
      }

      form.addEventListener('submit', (event) => {
        event.preventDefault()
        this.applyCollectionFilters(readParams(), defaultSort)
      })
    }

    isDefaultFilterQuery(params, defaultSort) {
      const keys = [...new Set([...params.keys()])]
      if (!keys.length) return true
      return keys.length === 1 && keys[0] === 'sort_by' && params.get('sort_by') === defaultSort
    }

    setFilterBusy(busy) {
      const button = this.root.querySelector('#collection-filters [data-hook="filter-form"] button[type="submit"]')
      if (!button) return
      if (busy) {
        button.disabled = true
        button.setAttribute('aria-busy', 'true')
      } else {
        button.disabled = false
        button.removeAttribute('aria-busy')
      }
    }

    closeFilterDrawer() {
      const filters = document.getElementById('collection-filters')
      if (!filters || !filters.classList.contains('on')) return
      if (window.jQuery) {
        window.jQuery(filters).removeClass('on')
        window.jQuery('[data-toggle="#collection-filters"]').removeClass('on')
        return
      }
      filters.classList.remove('on')
      document.querySelectorAll('[data-toggle="#collection-filters"]').forEach((trigger) => {
        trigger.classList.remove('on')
      })
    }

    collectionSections() {
      return [...this.root.querySelectorAll('[data-collection-url]')]
    }

    rememberFilterGrids() {
      this.collectionSections().forEach((section) => {
        const grid = section.querySelector('.bundler-direct-grid')
        if (!grid || this.filterGridHtml.has(grid)) return
        this.filterGridHtml.set(grid, grid.innerHTML)
      })
    }

    setSectionFilterState(section, hasProducts) {
      section.classList.toggle('is-filter-empty', !hasProducts)
      const navId = section.getAttribute('data-nav-target') || section.id
      const link = navId && this.root.querySelector(`[data-nav-link="${CSS.escape(navId)}"]`)
      if (link) link.setAttribute('aria-disabled', hasProducts ? 'false' : 'true')
    }

    refreshFilteredCards() {
      this.bindLiveCards()
      fetchCart()
        .then((cart) => refreshFromCart(this.root, cart))
        .catch(() => {})
      this.loadStampedBadges()
    }

    clearCollectionFilters() {
      this.root.classList.remove('is-filtered')
      const empty = this.root.querySelector('[data-hook="bundler-direct-filter-empty"]')
      if (empty) empty.hidden = true

      this.collectionSections().forEach((section) => {
        const grid = section.querySelector('.bundler-direct-grid')
        const original = grid && this.filterGridHtml.get(grid)
        if (grid && original != null) grid.innerHTML = original
        this.setSectionFilterState(section, true)
      })
      this.refreshFilteredCards()
    }

    async fetchCollectionAjax(collectionUrl, params) {
      const serialized = params.toString()
      const requestUrl = `${collectionUrl}?${serialized ? `${serialized}&` : ''}view=subscribe-ajax`
      const response = await fetch(requestUrl, {
        method: 'GET',
        headers: {
          Accept: '*/*',
          'X-Requested-With': 'XMLHttpRequest',
        },
        cache: 'no-store',
      })
      if (!response.ok) throw new Error('Unable to filter products')
      return response.text()
    }

    listFromHtml(html) {
      const template = document.createElement('template')
      template.innerHTML = html || ''
      return [...template.content.querySelectorAll('li')]
    }

    productHandle(item) {
      return item?.querySelector('[data-product-handle]')?.getAttribute('data-product-handle') || ''
    }

    productTitle(item) {
      return item?.querySelector('[data-title]')?.getAttribute('data-title')?.trim() || ''
    }

    productPrice(item) {
      const text = item?.querySelector('[data-hook="subscribe-current-price"]')?.textContent || ''
      const value = Number.parseFloat(text.replace(/[^0-9.]/g, ''))
      return Number.isFinite(value) ? value : 0
    }

    isLocalSort(sortBy) {
      return (
        sortBy === 'title-ascending' ||
        sortBy === 'title-descending' ||
        sortBy === 'price-ascending' ||
        sortBy === 'price-descending'
      )
    }

    sortItems(items, sortBy) {
      const direction = sortBy.endsWith('descending') ? -1 : 1
      const byPrice = sortBy.startsWith('price')
      items.sort((a, b) => {
        if (byPrice) return direction * (this.productPrice(a) - this.productPrice(b))
        return direction * this.productTitle(a).localeCompare(this.productTitle(b), undefined, { sensitivity: 'base' })
      })
      return items
    }

    // Alphabetical and price sorts reorder only the products already in that section.
    applyLocalCollectionSort(sortBy) {
      this.rememberFilterGrids()
      this.root.classList.remove('is-filtered')
      const empty = this.root.querySelector('[data-hook="bundler-direct-filter-empty"]')
      if (empty) empty.hidden = true

      this.collectionSections().forEach((section) => {
        const grid = section.querySelector('.bundler-direct-grid')
        if (!grid) return
        const items = this.sortItems(this.listFromHtml(this.filterGridHtml.get(grid) || ''), sortBy)
        grid.replaceChildren(...items)
        this.setSectionFilterState(section, items.length > 0)
      })
      this.refreshFilteredCards()
      this.closeFilterDrawer()
    }

    // Keep each section's own products. Sort and filter use that collection only.
    applySectionCollectionResult(section, html, hasFilters, sortBy) {
      const grid = section.querySelector('.bundler-direct-grid')
      if (!grid) return 0
      const originalItems = this.listFromHtml(this.filterGridHtml.get(grid) || '')
      const byHandle = new Map()
      originalItems.forEach((item) => {
        const handle = this.productHandle(item)
        if (handle && !byHandle.has(handle)) byHandle.set(handle, item)
      })

      const ordered = []
      const used = new Set()
      const take = (item) => {
        const handle = this.productHandle(item)
        if (!handle || used.has(handle) || !byHandle.has(handle)) return
        used.add(handle)
        const kept = byHandle.get(handle)
        kept.hidden = false
        kept.classList.add('is-filter-match')
        kept.classList.remove('is-filter-hidden')
        ordered.push(kept)
      }

      this.listFromHtml(html).forEach(take)
      if (!hasFilters) originalItems.forEach(take)
      if (this.isLocalSort(sortBy)) this.sortItems(ordered, sortBy)

      grid.replaceChildren(...ordered)
      this.setSectionFilterState(section, ordered.length > 0)
      return ordered.length
    }

    async applyCollectionFilters(params, defaultSort) {
      const filtering = !this.isDefaultFilterQuery(params, defaultSort)
      if (!filtering) {
        this.clearCollectionFilters()
        this.closeFilterDrawer()
        return
      }

      const sections = this.collectionSections()
      if (!sections.length) return

      const sortBy = params.get('sort_by') || ''
      const hasFilters = [...params.keys()].some((key) => key !== 'sort_by')
      if (!hasFilters && this.isLocalSort(sortBy)) {
        this.applyLocalCollectionSort(sortBy)
        return
      }

      this.rememberFilterGrids()
      this.setFilterBusy(true)
      try {
        const results = await Promise.all(
          sections.map(async (section) => {
            const collectionUrl = section.getAttribute('data-collection-url')
            const html = await this.fetchCollectionAjax(collectionUrl, params)
            return { section, html }
          })
        )

        let visibleCount = 0
        results.forEach(({ section, html }) => {
          visibleCount += this.applySectionCollectionResult(section, html, hasFilters, sortBy)
        })

        this.root.classList.add('is-filtered')
        const empty = this.root.querySelector('[data-hook="bundler-direct-filter-empty"]')
        if (empty) empty.hidden = visibleCount > 0
        this.refreshFilteredCards()
        this.closeFilterDrawer()
      } catch (error) {
        console.error('[bundle-builder-direct] Filter failed', error)
        window.alert(error.message || 'Unable to filter products')
      } finally {
        this.setFilterBusy(false)
      }
    }

    loadStampedBadges() {
      const load = () => {
        try {
          if (window.StampedFn && typeof window.StampedFn.loadBadges === 'function') {
            window.StampedFn.loadBadges()
            return true
          }
        } catch (e) {
          /* ignore */
        }
        return false
      }
      if (!load()) {
        let attempts = 0
        const poll = window.setInterval(() => {
          attempts += 1
          if (load() || attempts >= 20) window.clearInterval(poll)
        }, 500)
      } else {
        setTimeout(load, 1000)
      }
    }
  }

  ready(function () {
    document.querySelectorAll('[data-hook="bundle-builder-direct"]').forEach((root) => {
      BundleBuilderDirect.mount(root)
    })
  })
})()
